import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { userMessage, setChatStatus, setGenerating } from '../../store/slices/chatSlice';
import { addTemporaryChatGroup } from '../../store/slices/chatHistorySlice';
import useWebSocketContext from '../../hooks/useWebSocketContext'; 
import { v4 as uuidv4 } from 'uuid';
import ChatNavigationService from '../../services/ChatNavigationService';
import { isThinkingCapable } from '../../utils/modelCapabilities';

// Configuration options for different chat modes
interface ChatConfig {
  useAdvancedChatHistory?: boolean;
  enableDebugLogging?: boolean;
  fallbackToSimpleMode?: boolean;
}

// TypeScript interface for WebSocket chat parameters
interface ChatParams {
  message: string;
  stream: boolean;
  session_id: string;
  user_id: string | null;
  agent: string;
  model_name: string;
  thinking_mode?: boolean; // Optional parameter for thinking-capable models
  first_chat?: boolean;
  chat_group_id?: string;
}

const useChatEvents = (config: ChatConfig = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { status, error } = useSelector((state: RootState) => state.chat);
  const session = useSelector((state: RootState) => state.session);
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);  // Get selectedModel
  const thinkingMode = useSelector((state: RootState) => state.model.thinkingMode);  // Get thinking mode from Redux store
  const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup); // Get active chat group
  const researchMode = useSelector((state: RootState) => state.research.researchMode); // Get research mode
  const { sendToWs, isConnected } = useWebSocketContext();
  const [isReady, setIsReady] = useState(false);

  // Default configuration
  const {
    useAdvancedChatHistory = true,
    enableDebugLogging = true,
    fallbackToSimpleMode = true
  } = config;

  useEffect(() => {
    const ready = Boolean(session.session_id && isConnected);
    setIsReady(ready);
  }, [session, isConnected]);

  // Simple message sending (legacy mode from old file)
  const handleSendMessageSimple = async (message: string) => {
    if (!session.session_id || !isReady || !isConnected) {
      console.error('Chat not ready - cannot send message');
      return;
    }

    // Display original message in UI
    dispatch(userMessage(message));
    dispatch(setChatStatus('Generating...')); // Set chat status

    // Prepend research keyword if a research mode is active
    let messageToSend = message;
    if (researchMode === 'web') {
      messageToSend = `<webresearch>${message}`;
      console.log('🔍 Web Research mode active - message modified:', messageToSend);
    } else if (researchMode === 'deep') {
      messageToSend = `<deepresearch>${message}`;
      console.log('🔬 Deep Research mode active - message modified:', messageToSend);
    }

    // Build chat parameters with conditional thinking_mode for thinking-capable models
    const chatParams: ChatParams = {
      message: messageToSend,
      stream: true,
      session_id: session.session_id,
      user_id: session.user_id,
      agent: 'keai',
      model_name: selectedModel || 'gpt-5',  // Include model_name
    };

    // Add thinking_mode parameter for all thinking-capable models when enabled
    try {
      if (isThinkingCapable(selectedModel || '') && thinkingMode) {
        chatParams.thinking_mode = true;
      }
    } catch (error) {
      console.error('Error checking thinking capability:', error);
    }

    const msgToSend = JSON.stringify({
      method: 'chat',
      params: chatParams,
    });

    if (enableDebugLogging) {
      console.log('Simple mode - sending message:', { 
        session_id: session.session_id, 
        model: selectedModel, 
        thinking_mode: isThinkingCapable(selectedModel || '') ? thinkingMode : 'N/A (not thinking-capable)' 
      });
    }

    sendToWs(msgToSend);
  };

  // Advanced message sending with chat history (new file functionality)
  const handleSendMessageAdvanced = async (message: string) => {
    if (!session.session_id || !isReady || !isConnected) {
      console.error('Chat not ready - cannot send message');
      return;
    }

    let currentChatGroup = activeChatGroup;
    let first_chat = false;
    
    // Generate new chat group if none exists
    if (!currentChatGroup) {
      currentChatGroup = uuidv4();
      try {
        const chatNavService = ChatNavigationService.getInstance();
        chatNavService.updateURL(currentChatGroup);
        
        // Create temporary chat group with "New Chat" title immediately
        dispatch(addTemporaryChatGroup(currentChatGroup));
        
        first_chat = true;
      } catch (error) {
        console.warn('ChatNavigationService not available, continuing without URL update:', error);
        if (fallbackToSimpleMode) {
          return handleSendMessageSimple(message);
        }
      }
    }

    // Show user message immediately (original message in UI)
    dispatch(userMessage(message));
    dispatch(setGenerating(true)); // Start animation for AI response
    dispatch(setChatStatus('Generating...')); // Set chat status

    // Prepend research keyword if a research mode is active
    let messageToSend = message;
    if (researchMode === 'web') {
      messageToSend = `<webresearch>${message}`;
      console.log('🔍 Web Research mode active - message modified:', messageToSend);
    } else if (researchMode === 'deep') {
      messageToSend = `<deepresearch>${message}`;
      console.log('🔬 Deep Research mode active - message modified:', messageToSend);
    }

    // Debug: Show activeChatGroup status
    if (enableDebugLogging) {
      console.log('Current activeChatGroup:', currentChatGroup);
      console.log('Chat group status:', currentChatGroup ? 'Using chat group: ' + currentChatGroup : 'No active group');
    }

    const chatParams: ChatParams = {
      message: messageToSend,
      stream: true,
      session_id: currentChatGroup,
      user_id: session.user_id,
      agent: 'keai',
      first_chat: first_chat,
      model_name: selectedModel || 'gpt-5',  // Include model_name
    };

    // Add thinking_mode parameter for all thinking-capable models when enabled
    try {
      if (isThinkingCapable(selectedModel || '') && thinkingMode) {
        chatParams.thinking_mode = true;
      }
    } catch (error) {
      console.error('Error checking thinking capability:', error);
    }

    if (currentChatGroup) {
      chatParams.chat_group_id = currentChatGroup;
    }

    if (enableDebugLogging) {
      console.log('Advanced mode - chatParams:', {
        ...chatParams,
        thinking_mode_info: isThinkingCapable(selectedModel || '') ? `Thinking-capable model - thinking_mode: ${thinkingMode}` : 'N/A (not thinking-capable)'
      });
    }

    const msgToSend = JSON.stringify({
      method: 'chat',
      params: chatParams,
    });

    sendToWs(msgToSend);
  };

  // Main message handler that chooses between simple and advanced modes
  const handleSendMessage = async (message: string) => {
    try {
      if (useAdvancedChatHistory) {
        await handleSendMessageAdvanced(message);
      } else {
        await handleSendMessageSimple(message);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (fallbackToSimpleMode && useAdvancedChatHistory) {
        console.log('Falling back to simple mode due to error');
        await handleSendMessageSimple(message);
      }
    }
  };

  // Legacy method name for backward compatibility
  const sendUserInput = handleSendMessage;

  // Additional utility functions for chat management
  const getChatGroupInfo = () => {
    if (!useAdvancedChatHistory) return null;
    
    return {
      activeChatGroup,
      hasActiveChatGroup: Boolean(activeChatGroup),
      isFirstChat: !activeChatGroup,
    };
  };

  const resetChatGroup = () => {
    if (useAdvancedChatHistory) {
      // This would typically be handled by dispatching an action to reset the chat group
      // For now, we'll just log the intent
      console.log('Chat group reset requested');
    }
  };

  return {
    // Core functionality (available in both old and new files)
    status,
    error,
    isReady,
    sendUserInput, // Legacy method name
    handleSendMessage, // New method name
    
    // Advanced features (from new file)
    getChatGroupInfo,
    resetChatGroup,
    
    // Configuration info
    isAdvancedMode: useAdvancedChatHistory,
    config: {
      useAdvancedChatHistory,
      enableDebugLogging,
      fallbackToSimpleMode,
    },
  };
};

export default useChatEvents;
